import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "sonner";
import {
  Camera, Lightning, Armchair, Spinner, ArrowLeft,
  WhatsappLogo, DownloadSimple, ArrowsHorizontal, X, Sparkle, Share,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import AppShell from "@/components/AppShell";
import RegenerateButton from "@/components/RegenerateButton";
import { shareImage, shareFiles, shareVideo } from "@/lib/share";
import { isReaderMode } from "@/lib/iosReader";

function liveFileUrl(id, kind = "after") {
  const token = Cookies.get("vista_jwt");
  const qs = new URLSearchParams({ id, kind });
  if (token) qs.append("auth", token);
  return `${API}/live/file?${qs.toString()}`;
}

function liveBeforeAfterCardUrl(id) {
  const token = Cookies.get("vista_jwt");
  const qs = new URLSearchParams({ id });
  if (token) qs.append("auth", token);
  return `${API}/live/before-after-card?${qs.toString()}`;
}

function liveBeforeAfterVideoUrl(id, style = "slider") {
  const token = Cookies.get("vista_jwt");
  const qs = new URLSearchParams({ id, style });
  if (token) qs.append("auth", token);
  return `${API}/live/before-after-video?${qs.toString()}`;
}

const STAGE_STYLES = [
  { v: "moderno", l: "Moderno" },
  { v: "scandinavo", l: "Scandinavo" },
  { v: "minimalista", l: "Minimalista" },
  { v: "classico", l: "Classico" },
  { v: "elegante", l: "Elegante" },     /* iter v28 r39 */
  { v: "industriale", l: "Industriale" },
];

const ROOM_HINTS = [
  { v: "", l: "Decidi tu" },
  { v: "soggiorno", l: "Soggiorno" },
  // iter v28 r445 — User M1358: rinominato "Open space living-cucina" → "Cucina + soggiorno"
  // iter v28 r447 — User M1360: rimosso balcone, giardino → Esterno (patio)
  { v: "cucina e soggiorno open space", l: "Cucina + soggiorno" },
  { v: "camera da letto", l: "Camera" },
  { v: "cucina", l: "Cucina" },
  { v: "sala da pranzo", l: "Sala da pranzo" },
  { v: "studio", l: "Studio" },
  { v: "cameretta bambini", l: "Cameretta" },
  { v: "bagno", l: "Bagno" },
  { v: "terrazzo", l: "Terrazzo" },
  { v: "patio esterno", l: "Esterno (patio)" },
];

export default function LivePreview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState("stage"); // stage | restyle (enhance removed — not useful in visita)
  const [style, setStyle] = useState("moderno");
  const [room, setRoom] = useState("");
  const [changeFloor, setChangeFloor] = useState(false);
  const [floorStyle, setFloorStyle] = useState("parquet_chiaro");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // captured but not yet sent to AI

  const isPro = user?.plan === "pro" || user?.subscription_status === "referral";

  useEffect(() => {
    api.get("/live/quota").then(({ data }) => setQuota(data)).catch(() => {});
  }, []);

  const openCamera = () => fileInputRef.current?.click();

  // Step 1 — capture: only show preview, DO NOT call AI yet
  const onCapture = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-pick same file works
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);
    setPendingFile(file);
    setResult(null);
  };

  // Step 2 — runAi: triggered by explicit "Genera" button after user
  // confirmed mode + style + room
  const runAi = async () => {
    if (!pendingFile) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", pendingFile);
    if (mode === "stage") {
      fd.append("style", style);
      if (room) fd.append("room_hint", room);
    }
    if (mode === "restyle") {
      fd.append("style", style);
      if (room) fd.append("room_hint", room);
      fd.append("change_floor", String(!!changeFloor));
      if (changeFloor) fd.append("floor_style", floorStyle);
    }

    try {
      const endpoint = mode === "restyle" ? "/live/restyle" : "/live/stage";
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setResult(data);
      setQuota((q) => q ? { ...q, used: data.quota_cap > 0 ? data.quota_used_after : q.used, remaining: q.cap > 0 ? Math.max(0, q.cap - data.quota_used_after) : -1 } : q);
      // iter v28 r357 — User M1267: «in ogni foto modificata, in ogni arredo
      // nuovo apri un popup "Non ti piace? Rigenera"». Toast 9s con action.
      toast(mode === "restyle" ? "Arredo rinnovato · non ti piace?" : "Arredata · non ti piace?", {
        description: "Clicca Rigenera per provare un risultato diverso.",
        duration: 9000,
        action: {
          label: "Rigenera",
          onClick: () => {
            if (pendingFile) {
              // Re-trigger della stessa generazione con stessi parametri
              runAi();
            }
          },
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Servizio non disponibile, riprova";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setResult(null);
    setPendingFile(null);
  };

  const shareWhatsapp = async () => {
    if (!result) return;
    // iter v28 r356 — User feedback: niente testo "Foto migliorata con Pronto"
    await shareImage({
      url: liveFileUrl(result.id, "after"),
      filename: `pronto-after-${result.id.slice(0, 8)}.jpg`,
      title: "",
      text: "",
    });
  };

  const shareBeforeAfter = async () => {
    if (!result) return;
    await shareImage({
      url: liveBeforeAfterCardUrl(result.id),
      filename: `pronto-prima-dopo-${result.id.slice(0, 8)}.png`,
      title: "",
      text: "",
    });
  };

  const shareBeforeAfterVideo = async () => {
    if (!result) return;
    toast.loading("Preparo il video 6s…", { id: "va-video" });
    try {
      await shareVideo({
        url: liveBeforeAfterVideoUrl(result.id, "slider"),
        filename: `pronto-prima-dopo-${result.id.slice(0, 8)}.mp4`,
        title: "",
        text: "",
      });
      toast.dismiss("va-video");
    } catch {
      toast.error("Video non generato, riprova", { id: "va-video" });
    }
  };

  const shareProgressiveVideo = async () => {
    if (!result) return;
    // Mini-video 9s 1080x1080 step-by-step (5 tendine bottom→top)
    toast.loading("Preparo il video step-by-step (9s)…", { id: "va-prog" });
    try {
      await shareVideo({
        url: liveBeforeAfterVideoUrl(result.id, "progressive"),
        filename: `pronto-step-${result.id.slice(0, 8)}.mp4`,
        title: "Trasformazione step-by-step",
        text: "5 step di trasformazione — Pronto",
      });
      toast.dismiss("va-prog");
    } catch (e) {
      toast.error("Video non generato, riprova", { id: "va-prog" });
    }
  };

  const downloadAfter = async () => {
    if (!result) return;
    try {
      const res = await fetch(liveFileUrl(result.id, "after"));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pronto-live-${result.id.slice(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    } catch {
      toast.error("Download non riuscito");
    }
  };

  return (
    <AppShell title="Live · Mostra al cliente" breadcrumb="Live · Mostra al cliente">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1.5 mb-3"
            data-testid="live-back-btn"
          >
            <ArrowLeft size={14} weight="bold" /> Dashboard
          </button>
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold px-2.5 py-1 bg-[#F1F4ED]">
              <Sparkle size={11} weight="fill" /> Live
            </span>
            {isPro ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 bg-[#7C8B6E] text-[#FFFFFF]">Pro · illimitato</span>
            ) : quota ? (
              <span className="text-xs text-[#6B6A65]" data-testid="live-quota">
                {quota.remaining}/{quota.cap} oggi
              </span>
            ) : null}
          </div>
          <h1 className="font-editorial text-3xl sm:text-4xl tracking-tight text-[#1C1C1A] leading-tight">
            Sei in visita.<br />
            <span className="italic-accent text-[#7C8B6E]">Mostragli come diventerà.</span>
          </h1>
          <p className="mt-3 text-sm text-[#6B6A65] max-w-xl">
            Scatta una foto, scegli stile e tipo di stanza, premi <strong>Genera</strong>.
            In 5 secondi vedi il risultato e lo condividi col cliente.
          </p>
        </div>

        {/* Mode tabs — solo arreda vuoto / rinnova arredo (in visita non serve "migliora") */}
        <div className="flex gap-2 mb-5" role="tablist" data-testid="live-mode-tabs">
          <button
            onClick={() => { setMode("stage"); reset(); }}
            className={`flex-1 py-3 px-3 text-[12px] sm:text-sm font-medium rounded-full inline-flex items-center justify-center gap-1.5 transition-all ${mode === "stage" ? "bg-[#1C1C1A] text-[#FFFFFF]" : "bg-[#F1F4ED] text-[#1C1C1A] hover:bg-[#F1F4ED]"}`}
            data-testid="live-mode-stage"
          >
            <Armchair size={14} weight={mode === "stage" ? "fill" : "regular"} />
            Arreda vuoto
          </button>
          <button
            onClick={() => { setMode("restyle"); reset(); }}
            className={`flex-1 py-3 px-3 text-[12px] sm:text-sm font-medium rounded-full inline-flex items-center justify-center gap-1.5 transition-all ${mode === "restyle" ? "bg-[#1C1C1A] text-[#FFFFFF]" : "bg-[#F1F4ED] text-[#1C1C1A] hover:bg-[#F1F4ED]"}`}
            data-testid="live-mode-restyle"
          >
            <Sparkle size={14} weight={mode === "restyle" ? "fill" : "regular"} />
            Rinnova arredo
          </button>
        </div>

        {/* Style picker — sempre visibile (stage o restyle) */}
        {!result && (
          <div className="mb-5 space-y-4" data-testid="live-style-row">
            {mode === "restyle" && (
              <p className="text-[12px] text-[#6B6A65] leading-relaxed">
                <strong className="text-[#1C1C1A]">Rinnova arredo:</strong> stessa stanza, mobili nuovi.
                Muri, finestre e porte invariati. Per immobili antiquati di cui rifare il look.
              </p>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">Diventerà...</div>
              <div className="flex flex-wrap gap-2">
                {ROOM_HINTS.map((r) => (
                  <button
                    key={r.v || "auto"}
                    onClick={() => setRoom(r.v)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-all ${room === r.v ? "bg-[#7C8B6E] text-[#FFFFFF] border-[#7C8B6E]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                    data-testid={`live-room-${r.v || "auto"}`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A65] mb-2">Stile arredo</div>
              <div className="flex flex-wrap gap-2">
                {STAGE_STYLES.map((s) => (
                  <button
                    key={s.v}
                    onClick={() => setStyle(s.v)}
                    className={`px-3.5 py-1.5 text-xs rounded-full border transition-all ${style === s.v ? "bg-[#1C1C1A] text-[#FFFFFF] border-[#1C1C1A]" : "bg-[#FFFFFF] text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                    data-testid={`live-style-${s.v}`}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>
            {mode === "restyle" && (
              <div>
                <label className="flex items-center gap-2 cursor-pointer text-[12.5px] text-[#1C1C1A]" data-testid="live-floor-toggle-label">
                  <input type="checkbox" checked={changeFloor} onChange={(e) => setChangeFloor(e.target.checked)} className="accent-[#7C8B6E]" data-testid="live-floor-toggle" />
                  <span>Cambia anche il <strong>pavimento</strong></span>
                </label>
                {changeFloor && (
                  <div className="mt-2 ml-6 flex flex-wrap gap-2" data-testid="live-floor-row">
                    {[
                      { v: "parquet_chiaro", l: "Parquet chiaro" },
                      { v: "parquet_scuro", l: "Parquet scuro" },
                      { v: "gres_pietra", l: "Gres effetto pietra" },
                      { v: "resina_chiara", l: "Resina chiara" },
                      { v: "cemento_lucido", l: "Cemento lucido" },
                    ].map((f) => (
                      <button
                        key={f.v} onClick={() => setFloorStyle(f.v)}
                        className={`px-3 py-1.5 text-[11px] rounded-full border transition-all ${floorStyle === f.v ? "bg-[#7C8B6E] text-white border-[#7C8B6E]" : "bg-white text-[#1C1C1A] border-[#E5E7E0] hover:border-[#7C8B6E]"}`}
                        data-testid={`live-floor-${f.v}`}
                      >
                        {f.l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Hidden camera input — iter v29 r220: rimosso capture="environment"
            così su mobile web l'utente può scegliere fra Scatta foto e Galleria
            (l'OS mostra automaticamente lo sheet su iOS Safari + Android Chrome). */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onCapture}
          className="hidden"
          data-testid="live-file-input"
        />

        {/* Stage 1: empty → big camera button */}
        {!previewUrl && !result && (
          <button
            onClick={openCamera}
            className="w-full aspect-[4/5] sm:aspect-[16/10] border-2 border-dashed border-[#E5E7E0] hover:border-[#7C8B6E] hover:bg-[#FFFFFF] rounded-2xl flex flex-col items-center justify-center gap-3 transition-all group"
            data-testid="live-camera-btn"
          >
            <div className="w-16 h-16 rounded-full bg-[#7C8B6E] text-[#FFFFFF] flex items-center justify-center group-hover:scale-110 transition-transform">
              <Camera size={28} weight="fill" />
            </div>
            <div className="text-lg font-medium text-[#1C1C1A]">Scatta una foto</div>
            <div className="text-xs text-[#6B6A65] text-center px-6">
              Tocca per aprire la fotocamera. Inquadra la stanza, scatta, attendi 5 secondi.
            </div>
          </button>
        )}

        {/* Stage 2: photo captured, waiting for "Genera" — user can still tweak style/room */}
        {previewUrl && !loading && !result && (
          <div className="space-y-4" data-testid="live-pending">
            <div className="relative rounded-2xl overflow-hidden bg-[#1C1C1A] aspect-[4/5] sm:aspect-[16/10]">
              <img
                src={previewUrl}
                alt="Anteprima foto"
                className="w-full h-full object-cover"
                data-testid="live-pending-preview"
              />
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#FFFFFF] bg-[#1C1C1A]/70 backdrop-blur-sm px-2.5 py-1 rounded-full">
                <Camera size={11} weight="fill" /> Pronta da generare
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={runAi}
                className="col-span-2 bg-[#1C1C1A] text-[#FFFFFF] py-3.5 px-4 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#2A2A28] active:scale-[0.99] transition-all"
                data-testid="live-generate-btn"
              >
                <Sparkle size={18} weight="fill" />
                {mode === "restyle" ? `Genera · rinnova in ${style}` : `Genera · arreda in ${style}`}
              </button>
              <button
                onClick={() => { reset(); openCamera(); }}
                className="bg-[#F1F4ED] text-[#1C1C1A] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#E5E7E0] transition-all"
                data-testid="live-reshoot"
              >
                <Camera size={16} weight="bold" /> Riscatta
              </button>
              <button
                onClick={reset}
                className="bg-white border border-[#E5E7E0] text-[#6B6A65] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#F1F4ED] hover:text-[#1C1C1A] transition-all"
                data-testid="live-cancel"
              >
                <X size={14} /> Annulla
              </button>
            </div>

            <p className="text-[11px] text-[#6B6A65] text-center">
              Cambia stile o stanza qui sopra prima di generare. La foto resta in memoria.
            </p>
          </div>
        )}

        {/* Stage 3: loading state with local preview */}
        {previewUrl && loading && (
          <div className="relative rounded-2xl overflow-hidden bg-[#1C1C1A] aspect-[4/5] sm:aspect-[16/10]" data-testid="live-loading">
            <img src={previewUrl} alt="Anteprima" className="w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#FFFFFF]">
              <Spinner size={36} className="animate-spin" />
              <div className="text-sm font-medium">
                {mode === "restyle"
                  ? `Sto rinnovando l'arredo${changeFloor ? " e il pavimento" : ""} (${style})...`
                  : `Sto arredando${room ? ` come ${room}` : ""} in stile ${style}...`}
              </div>
              <div className="text-xs opacity-70">5–10 secondi</div>
            </div>
          </div>
        )}

        {/* Stage 3: result with before/after slider */}
        {result && previewUrl && (
          <div className="space-y-4" data-testid="live-result">
            <div className="rounded-2xl overflow-hidden">
              <BeforeAfterSlider
                beforeSrc={previewUrl}
                afterSrc={liveFileUrl(result.id, "after")}
                height="h-[55vh] sm:h-[60vh]"
                fit="contain"
              />
            </div>
            <div className="flex items-center justify-between gap-3 text-xs text-[#6B6A65] flex-wrap">
              <span className="inline-flex items-center gap-1.5"><ArrowsHorizontal size={14} /> Trascina lo slider per confrontare</span>
              <RegenerateButton
                onRegenerate={runAi}
                disabled={loading}
                kind={mode === "plan" ? "plan" : "photo"}
                testid="live-regenerate"
              />
            </div>

            {/* Action bar */}
            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={shareBeforeAfter}
                className="col-span-2 bg-[#25D366] text-[#FFFFFF] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                data-testid="live-share-prima-dopo"
              >
                <WhatsappLogo size={18} weight="fill" /> Manda Prima/Dopo
              </button>
              <button
                onClick={shareBeforeAfterVideo}
                className="bg-[#1C1C1A] text-[#FFFFFF] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                data-testid="live-share-video"
              >
                <Sparkle size={16} weight="fill" /> Video 6s (slider)
              </button>
              <button
                onClick={shareProgressiveVideo}
                className="bg-[#7C8B6E] text-[#FFFFFF] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                data-testid="live-share-video-progressive"
              >
                <Sparkle size={16} weight="fill" /> Video 9s (5 step)
              </button>
              <button
                onClick={shareWhatsapp}
                className="bg-white border border-[#E5E7E0] text-[#1C1C1A] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#F1F4ED] transition-colors"
                data-testid="live-share-after-only"
              >
                <Share size={16} weight="regular" /> Solo dopo
              </button>
              <button
                onClick={downloadAfter}
                className="bg-white border border-[#E5E7E0] text-[#1C1C1A] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#F1F4ED] transition-colors"
                data-testid="live-download"
              >
                <DownloadSimple size={16} weight="regular" /> Scarica
              </button>
              <button
                onClick={() => { reset(); openCamera(); }}
                className="col-span-2 bg-[#F1F4ED] text-[#1C1C1A] py-3 px-4 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#E5E7E0] transition-all"
                data-testid="live-new-shot"
              >
                <Camera size={18} weight="bold" /> Nuova foto
              </button>
            </div>

            <button
              onClick={reset}
              className="w-full text-xs text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center justify-center gap-1.5 py-2"
              data-testid="live-reset"
            >
              <X size={12} /> Annulla
            </button>
          </div>
        )}

        {/* Free-tier nudge to upgrade — iter 2026-06-12 Apple 3.1.1: nascosto su iOS native */}
        {!isPro && !isReaderMode && quota && quota.remaining <= 1 && (
          <div className="mt-6 p-4 bg-[#FFFFFF] border border-[#E5E7E0] rounded-xl text-center" data-testid="live-upgrade-nudge">
            <div className="text-sm text-[#1C1C1A] font-medium">
              {quota.remaining === 0 ? "Hai finito le anteprime di oggi" : "Ti resta 1 anteprima oggi"}
            </div>
            <div className="text-xs text-[#6B6A65] mt-1 mb-3">
              Con Pro hai anteprime live illimitate durante le visite.
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="text-sm bg-[#7C8B6E] text-[#FFFFFF] px-4 py-2 rounded-full hover:bg-[#5C6E50] transition-colors"
              data-testid="live-upgrade-btn"
            >
              Passa a Pro · 49€/mese
            </button>
          </div>
        )}
      </AppShell>
  );
}
